import {autoRegister, resolve} from "../../container";
import {FormSessionId} from "./formSessionId";
import {RequestSugarFactory} from "../common/requestSugar";
import {type SecuredExecutorResponse, SecuredRequestExecutor} from "../../module/awswaf/securedRequestExecutor";

const UUID_LENGTH = 36;

export class UploadSecuredResponse implements SecuredExecutorResponse {
    public constructor(public request: XMLHttpRequest) {
    }

    public getStatus(): number {
        return this.request.status;
    }

    public getHeader(name: string): string | undefined {
        return this.request.getResponseHeader(name) ?? undefined;
    }
}

export class UploadResponse {
    public constructor(public uploadKey: string, public status: number) {
    }
}

export class UploadResponseError extends Error {
    public constructor(public status: number) {
        super();
    }
}

export class UploadDeleteRequest {
    public constructor(
        public fileKey: string,
        public configId: string,
        public formId: string,
        public lang: string
    ) {
    }
}

export class UploadRequest {
    public constructor(
        public file: File,
        public configId: string,
        public formId: string,
        public onProgress: (progress: number) => void,
        public lang: string
    ) {
    }
}

@autoRegister()
export class UploadService {

    private UPLOAD_ENDPOINT: string = "/enbw-eop/eop-app-form/rest/stable/attachments/upload";
    private DELETE_ENDPOINT: string = "/enbw-eop/eop-app-form/rest/stable/attachments/delete";

    public constructor(
        private sessionIdGenerator: FormSessionId = resolve(FormSessionId),
        private securedRequestExecutor: SecuredRequestExecutor = resolve(SecuredRequestExecutor)
    ) {
    }

    public async upload(request: UploadRequest): Promise<UploadResponse> {
        const uploadRequest = (): Promise<UploadSecuredResponse> => new Promise<UploadSecuredResponse>((res, _rej) => {
            const httpRequest = new XMLHttpRequest();
            const data = new FormData();
            data.append("file", request.file);

            httpRequest.onload = () => res(new UploadSecuredResponse(httpRequest));
            httpRequest.upload.onprogress = (event: ProgressEvent) => request.onProgress(100 / event.total * event.loaded);
            httpRequest.open("POST", `${this.UPLOAD_ENDPOINT}/${request.configId}/${request.lang}/${(this.getSessionId(request.formId))}`);
            httpRequest.setRequestHeader("EOP-Sugar", RequestSugarFactory.produce());
            httpRequest.send(data);
        });

        const securedResponse = await this.securedRequestExecutor.execute<UploadSecuredResponse>(uploadRequest);
        const success = securedResponse.request.status === 200
            && typeof securedResponse.request.response === "string"
            && securedResponse.request.response.length === UUID_LENGTH;

        if (success) {
            return new UploadResponse(securedResponse.request.response, securedResponse.request.status);
        } else {
            throw new UploadResponseError(securedResponse.request.status);
        }
    }

    public async delete(request: UploadDeleteRequest): Promise<void> {
        const postRequest = (): Promise<Response> => fetch(`${this.DELETE_ENDPOINT}/${request.configId}/${request.lang}/${this.getSessionId(request.formId)}/${request.fileKey}`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json;charset=UTF-8",
                    "EOP-Sugar": RequestSugarFactory.produce()
                },
                body: ""
            });
        const response = await this.securedRequestExecutor.execute(postRequest);

        if (!response.ok) {
            throw new Error(`Response failed with status ${response.status}: ${response.statusText}`);
        }
    }

    private getSessionId(formId: string): string {
        return this.sessionIdGenerator.get(formId);
    }
}